import CryptoJS from "crypto-js";

const SECRET_KEY = CryptoJS.enc.Utf8.parse(
  process.env.REACT_APP_SECRET_KEY?.substring(0, 32)
);

export const encryptData = (data) => {
  return CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(data), SECRET_KEY, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
};

export const decryptData = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    console.log(bytes,"bytes");
    let data = CryptoJS.enc.Utf8.stringify(bytes)
    console.log( data, "crypto");
    
    return CryptoJS.enc.Utf8.stringify(bytes);
  } catch (error) {
    console.error("Decryption Error:", error?.message);
    return null;
  }
};


const ALGORITHM = "AES-GCM";

const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";

  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });

  return btoa(binary);
};

const importKey = async () => {
  const keyBase64 = process.env.REACT_APP_ADMIN_VAULT_SECRET_KEY;


  if (!keyBase64) {
    throw new Error("REACT_APP_ADMIN_VAULT_SECRET_KEY missing");
  }

  const keyBytes = Uint8Array.from(
    atob(keyBase64),
    (c) => c.charCodeAt(0)
  );

  return window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    "AES-GCM",
    false,
    ["encrypt", "decrypt"]
  );
};

export const encryptVaultSecret = async (plainText) => {
  try {
    const key = await importKey();

    const iv = window.crypto.getRandomValues(
      new Uint8Array(12)
    );

    const encoded = new TextEncoder().encode(plainText);

    const encryptedBuffer =
      await window.crypto.subtle.encrypt(
        {
          name: ALGORITHM,
          iv,
          tagLength: 128,
        },
        key,
        encoded
      );

    const encryptedBytes = new Uint8Array(encryptedBuffer);

    const authTag = encryptedBytes.slice(
      encryptedBytes.length - 16
    );

    const cipherText = encryptedBytes.slice(
      0,
      encryptedBytes.length - 16
    );

    return [
      arrayBufferToBase64(iv),
      arrayBufferToBase64(authTag),
      arrayBufferToBase64(cipherText),
    ].join(":");
  } catch (error) {
    console.error("Vault Encryption Error:", error);
    return null;
  }
};

export const decryptVaultSecret = async (encryptedSecret) => {
  try {
    const [ivBase64, authTagBase64, encryptedBase64] =
      encryptedSecret.split(":");

    const iv = new Uint8Array(
      base64ToArrayBuffer(ivBase64)
    );

    const authTag = new Uint8Array(
      base64ToArrayBuffer(authTagBase64)
    );

    const encrypted = new Uint8Array(
      base64ToArrayBuffer(encryptedBase64)
    );

    const combined = new Uint8Array(
      encrypted.length + authTag.length
    );

    combined.set(encrypted);
    combined.set(authTag, encrypted.length);

    const key = await importKey();

    const decrypted =
      await window.crypto.subtle.decrypt(
        {
          name: ALGORITHM,
          iv,
          tagLength: 128,
        },
        key,
        combined
      );

    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error("Vault Decryption Error:", error);
    return null;
  }
};


const importDocumentKey = async () => {
  const keyBase64 = process.env.REACT_APP_ADMIN_VAULT_DOCUMENT_KEY;

  if (!keyBase64) {
    throw new Error("REACT_APP_ADMIN_VAULT_DOCUMENT_KEY missing");
  }

  const keyBytes = Uint8Array.from(atob(keyBase64), (c) => c.charCodeAt(0));

  if (keyBytes.length !== 32) {
    throw new Error("REACT_APP_ADMIN_VAULT_DOCUMENT_KEY must be 32 bytes");
  }

  return window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    "AES-GCM",
    false,
    ["encrypt", "decrypt"]
  );
};

export const encryptVaultDocument = async (file) => {
  try {
    const key = await importDocumentKey();

    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const fileBuffer = await file.arrayBuffer();

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        tagLength: 128,
      },
      key,
      fileBuffer
    );

    const encryptedBytes = new Uint8Array(encryptedBuffer);

    const cipherText = encryptedBytes.slice(0, encryptedBytes.length - 16);
    const authTag = encryptedBytes.slice(encryptedBytes.length - 16);

    // Backend expects: iv + authTag + encryptedData
    const finalBuffer = new Uint8Array(
      iv.length + authTag.length + cipherText.length
    );

    finalBuffer.set(iv, 0);
    finalBuffer.set(authTag, iv.length);
    finalBuffer.set(cipherText, iv.length + authTag.length);

    return new File([finalBuffer], file.name, {
      type: file.type || "application/octet-stream",
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error("Vault Document Encryption Error:", error);
    return null;
  }
};

export const decryptVaultDocument = async (encryptedArrayBuffer) => {
  try {
    const key = await importDocumentKey();

    const encryptedBytes = new Uint8Array(encryptedArrayBuffer);

    const iv = encryptedBytes.slice(0, 12);
    const authTag = encryptedBytes.slice(12, 28);
    const cipherText = encryptedBytes.slice(28);

    const combined = new Uint8Array(cipherText.length + authTag.length);
    combined.set(cipherText, 0);
    combined.set(authTag, cipherText.length);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv,
        tagLength: 128,
      },
      key,
      combined
    );

    return decryptedBuffer;
  } catch (error) {
    console.error("Vault Document Decryption Error:", error);
    return null;
  }
};